/**
 * @file: ChatThreadRow.tsx
 *
 * One thread row in the chat list: avatar, title (pinned/unread weight),
 * relative time, preview line (group sender prefix, @-mention flag, unread
 * voice highlight), mute + unread badges. Plus the thin list separator.
 * Presentational.
 */

import { Typography } from '@euler/app/components/typography';
import { PresenceDot } from '@euler/app/flows/chat/components/PresenceDot';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { useIsOnline } from '@euler/app/flows/chat/lib/session/hooks/usePresence';
import { OrderRoomSnapshotCard } from '@euler/app/flows/chat/pages/thread-list/components/OrderRoomSnapshotCard';
import { chatMessagePreview } from '@euler/app/flows/chat/utils/chatMessagePreview';
import {
  chatThreadAvatar,
  chatThreadIsPinned,
  chatThreadTitle,
} from '@euler/app/flows/chat/utils/chatThreadDisplay';
import { useInboxListTheme } from '@euler/app/flows/inbox/lib/listTheme';
import { Avatar } from '@euler/components/Avatar';
import { UnreadBadge } from '@euler/components/UnreadBadge';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatThreadItem } from '@euler/model/chat';
import { BellOff } from 'lucide-react-native';
import { FC, memo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

function relativeTimeStr(dateStr: string | undefined | null): string {
  if (!dateStr) return '';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  if (diff < 60_000) return '刚刚';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}分钟前`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}小时前`;
  if (diff < 604800_000) return `${Math.floor(diff / 86400_000)}天前`;
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export const ChatThreadRow: FC<{
  thread: ChatThreadItem;
  active?: boolean;
  /** Render the bottom hairline edge-to-edge (marks the pinned-section
   * boundary); otherwise it's inset past the avatar, WeChat-style. */
  fullWidthDivider?: boolean;
  onPress: () => void;
}> = memo(({ thread, active, fullWidthDivider, onPress }) => {
  const { rpx } = useResponsiveContext();
  const theme = useInboxListTheme();
  const { me } = useChatSession();
  const selfName = me.user.name;
  const myPlatformUserId = me.platformUserId;
  const title = chatThreadTitle(thread, selfName);
  const avatar = chatThreadAvatar(thread);
  // Render the last-message line from the structured payload (the FE owns
  // formatting); fall back to the server `preview` string only when the
  // payload is absent (legacy rows).
  const preview =
    chatMessagePreview(thread.latestMessage?.payload, myPlatformUserId) ??
    thread.latestMessage?.preview ??
    '';
  const hasUnread = thread.unreadCount > 0;
  // An unread voice message stands out in red until it's opened/played.
  const isUnreadAudio =
    hasUnread && thread.latestMessage?.payload?.type === 'audio';
  const hasMention = thread.unreadMentionCount > 0;
  // Group rows prefix the sender ("x4dev: …") like WeChat so you can tell
  // who spoke. Skip for your own latest message and for system notices
  // (already full sentences); private rows show the bare preview.
  const isGroup = thread.room?.type === 'group';
  const latestAuthor = thread.latestMessage?.authorName?.trim() || '';
  const isSystemLatest = thread.latestMessage?.payload?.type === 'notification';
  const previewLine =
    isGroup && latestAuthor && latestAuthor !== selfName && !isSystemLatest
      ? `${latestAuthor}: ${preview}`
      : preview;
  const isPinned = chatThreadIsPinned(thread);
  const isMuted = thread.mutedAt != null;
  // Online dot only for private threads (a 1:1 has a single peer); group
  // rows have no `peer` and get no dot.
  const peerOnline = useIsOnline(thread.peer);

  // Row / divider / text colors all flow from the inbox-list theme so
  // the chat list and the feed list stay aligned across desktop (tinted
  // grays) and mobile (plain white). Tier mapping is unchanged — pinned
  // takes `otherPinned`, unpinned takes `unpinned`. (Chat has no
  // scope-pinned "essentials" tier, so `defaultPinned` is unused here.)
  const selected = !!active;
  const rowBg = selected
    ? theme.row.selected
    : isPinned
    ? theme.row.otherPinned
    : theme.row.unpinned;
  const dividerColor = fullWidthDivider
    ? theme.divider.section
    : isPinned
    ? theme.divider.otherPinned
    : theme.divider.unpinned;
  const titleColor = selected ? theme.text.titleSelected : theme.text.title;
  const timeColor = selected ? theme.text.timeSelected : theme.text.time;
  const previewColor = selected
    ? theme.text.previewSelected
    : isUnreadAudio
    ? '#ef4444'
    : hasUnread
    ? theme.text.previewUnread
    : theme.text.previewRead;
  const mentionColor = selected ? theme.text.titleSelected : '#ef4444';
  const mutedIconColor = selected
    ? theme.text.mutedIconSelected
    : theme.text.mutedIcon;

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.6}
      css={`
        position: relative;
        flex-direction: row;
        align-items: center;
        padding: ${rpx(12)}px ${rpx(16)}px;
        background-color: ${rowBg};
      `}
    >
      <View style={{ marginRight: rpx(12) }}>
        <Avatar
          uri={avatar.uri}
          name={avatar.name}
          textStyle={{
            fontFamily: FontFamily.NotoSans.Medium,
            fontSize: rpx(16),
            lineHeight: rpx(20),
          }}
          style={{
            width: rpx(44),
            height: rpx(44),
            borderRadius: rpx(22),
          }}
        />
        {peerOnline ? (
          <PresenceDot style={{ position: 'absolute', right: 0, bottom: 0 }} />
        ) : null}
      </View>
      <View
        css={`
          flex: 1;
        `}
      >
        <View
          css={`
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <Typography.Text
            size={rpx(15)}
            color={titleColor}
            numberOfLines={1}
            style={{
              flex: 1,
              fontFamily: hasUnread
                ? FontFamily.NotoSans.Bold
                : FontFamily.NotoSans.Medium,
            }}
          >
            {title}
          </Typography.Text>
          <Typography.Text size={rpx(11)} color={timeColor}>
            {relativeTimeStr(thread.latestActivityAt)}
          </Typography.Text>
        </View>
        {thread.room?.orderSnapshot ? (
          <OrderRoomSnapshotCard
            snapshot={thread.room.orderSnapshot}
            selected={selected}
          />
        ) : null}
        <View
          css={`
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            margin-top: ${rpx(2)}px;
          `}
        >
          <Typography.Text
            size={rpx(12)}
            color={previewColor}
            numberOfLines={2}
            style={{ flex: 1, marginRight: rpx(8) }}
          >
            {hasMention ? (
              <Typography.Text size={rpx(12)} color={mentionColor}>
                [有人@我]{' '}
              </Typography.Text>
            ) : null}
            {previewLine}
          </Typography.Text>
          {isMuted ? (
            // Muted: a small red dot when there's something unread, else just
            // the grey muted-bell — never a count (WeChat-style).
            hasUnread ? (
              <UnreadBadge count={thread.unreadCount} dotOnly />
            ) : (
              <BellOff color={mutedIconColor} size={rpx(14)} strokeWidth={2} />
            )
          ) : hasUnread ? (
            <UnreadBadge count={thread.unreadCount} />
          ) : null}
        </View>
      </View>
      {/* Bottom hairline lives INSIDE the row's bottom edge (WeChat-style):
          the last row of each group keeps its line, and the selected row
          paints clean (no divider over the solid fill). */}
      {selected ? null : (
        <View
          style={{
            position: 'absolute',
            left: fullWidthDivider ? 0 : rpx(72),
            right: 0,
            bottom: 0,
            height: StyleSheet.hairlineWidth,
            backgroundColor: dividerColor,
          }}
        />
      )}
    </TouchableOpacity>
  );
});
